import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { LinkTarget } from '@/common/types'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'

import styles from './_styles/card.scss?inline'

/**
 * `dap-ds-card`
 * @summary A card is a container for content, actions, and images in a consistent format.
 * @element dap-ds-card
 * @title - Card
 *
 * @property {'sm' | 'md' | 'lg'} size - The size of the card title. Default is `sm`.
 *
 * @slot - The content of the card.
 *
 * @csspart base - The main card container.
 *
 * @cssproperty --dds-card-padding - The padding of the card. Default is `var(--dds-spacing-400)`.
 * @cssproperty --dds-card-border-radius - The border radius of the card. Default is `var(--dds-radius-large)`.
 * @cssproperty --dds-card-border-width - The border width of the card. Default is `var(--dds-border-width-base)`.
 * @cssproperty --dds-card-border-color - The border color of the card. Default is `var(--dds-border-neutral-divider)`.
 * @cssproperty --dds-card-background - The background color of the card. Default is `var(--dds-background-neutral-base)`.
 * @cssproperty --dds-card-hover-border-color - The border color when hovering over the card. Default is `var(--dds-border-brand-base)`.
 * @cssproperty --dds-card-active-border-color - The border color when the card is active. Default is `var(--dds-border-brand-medium)`.
 * @cssproperty --dds-card-shadow - The box shadow of the card. Default is `none`.
 * @cssproperty --dds-card-hover-shadow - The box shadow when hovering over the card. Default is `none`.
 * @cssproperty --dds-card-transition-duration - The duration of the card's transitions. Default is `var(--dds-transition-medium)`.
 * @cssproperty --dds-card-transition-timing - The timing function of the card's transitions. Default is `var(--dds-easing-ease-in-out)`.
 */
@customElement('dap-ds-card')
export default class DapDSCard extends SizedMixin(DdsElement) {
  /** Whether the card is interactive. Default is false. If true, the card will be rendered as an anchor element. */
  @property({ type: Boolean }) interactive = false
  /**
   * The render as type of the card, only applicable when interactive.
   * @type {'a' | 'button'}
   */
  @property({ type: String }) renderAs: 'a' | 'button' = 'a'
  /** Whether the card is disabled. */
  @property({ type: Boolean, reflect: true }) disabled = false
  /** Removes the border around the card */
  @property({ type: Boolean }) noBorder = false
  /** Removes the padding around the card */
  @property({ type: Boolean }) noPadding = false

  /** The link target of the card
   * @type { '_blank' | '_self' | '_parent' | '_top' }
   */
  @property() target: LinkTarget = '_self'

  /** The URL of the card. */
  @property() href: string

  /** The rel of the card link. */
  @property() rel = 'noreferrer noopener'

  static readonly styles = unsafeCSS(styles)

  override focus(options?: FocusOptions) {
    if (this.interactive) {
      ;(this.shadowRoot?.querySelector('.card') as HTMLElement)?.focus(options)
    }
  }

  render() {
    return html`
      ${this.interactive
        ? html`<${unsafeStatic(this.renderAs)}
            part="base"
            class=${clsx('card', {
              'card--interactive': true,
              'card--disabled': this.disabled,
              'card--no-border': this.noBorder,
              'card--no-padding': this.noPadding,
              [`card--${this.size}`]: true,
            })}
            aria-label=${ifDefined(this.ariaLabel) || ''}
            target=${this.target}
            href=${ifDefined(this.href)}
            rel=${this.target}>
            <div
              part="high-contrast"
              class=${clsx({
                'card__highcontrast': true,
                'card__highcontrast--hover': true,
                ['card__highcontrast--noborder']: !this.noBorder,
              })}></div>

            <slot></slot>
          </${unsafeStatic(this.renderAs)}>`
        : html`<div
            part="base"
            class="${clsx('card', {
              [`card--${this.size}`]: true,
              'card--disabled': this.disabled,
              'card--no-border': this.noBorder,
            })}">
            <div
              part="high-contrast"
              class=${clsx({
                'card__highcontrast': true,
                'card__highcontrast--hover': true,
                ['card__highcontrast--noborder']: !this.noBorder,
              })}></div>
            <slot></slot>
          </div>`}
    `
  }
}
